import {
  PRODUCT_CARDS_MAX,
  PRODUCT_CARDS_META_KEY,
  productCardsText,
  type ProductCard as ProductCardData,
} from '@inbx/shared/schemas';
import { useState } from 'react';

import {
  useCommerceContext,
  useCommerceQuery,
  useLinkCommerceCustomer,
  useSendMessage,
  useUnlinkCommerceCustomer,
  type CommerceContextRow,
  type CommerceCustomerRow,
  type CommerceMoney,
  type CommerceOrderRow,
  type CommerceProductRow,
  type CommerceQuickAction,
} from '../api/queries.js';
import { Button } from '../components/ui/index.js';

/**
 * ПАНЕЛЬ ТОРГОВЫХ ДАННЫХ В КАРТОЧКЕ ТИКЕТА
 *
 * Показывает покупателя, его заказы и товары — из магазина продавца, из
 * Битрикса, из WooCommerce, из чего угодно. Панель НЕ знает, откуда данные:
 * сервер уже привёл их к общему виду. Поэтому добавление новой торговой
 * системы не требует ни строчки здесь.
 *
 * Ошибка источника показывается тут же, а не прячется: оператор должен
 * понимать разницу между «заказов нет» и «магазин не ответил» — во втором
 * случае клиенту нельзя говорить «у вас нет заказов».
 */

interface Props {
  workspaceSlug: string | undefined;
  ticketId: string;
  /** Вставить текст в поле ответа оператора */
  onInsertText: (text: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  new: 'новый',
  paid: 'оплачен',
  processing: 'собирается',
  shipped: 'отправлен',
  delivered: 'доставлен',
  cancelled: 'отменён',
  refunded: 'возврат',
  unknown: 'уточняется',
};

const STATUS_TONE: Record<string, string> = {
  new: '#E6F1FB',
  paid: '#E1F5EE',
  processing: '#FAEEDA',
  shipped: '#E6F1FB',
  delivered: '#E1F5EE',
  cancelled: '#FBE9E7',
  refunded: '#FBE9E7',
  unknown: '#F1EFE8',
};

export function CommercePanel(props: Props): JSX.Element | null {
  const ctxQ = useCommerceContext(props.workspaceSlug, props.ticketId);

  if (ctxQ.isLoading) {
    return (
      <div className="inbx-contact__section">
        <div className="inbx-contact__section-h">Покупки</div>
        <div className="inbx-contact__row-label">Загружаем…</div>
      </div>
    );
  }

  const sources = ctxQ.data ?? [];
  // Ни одного источника не подключено — панели просто нет, лишний пустой
  // блок в карточке никому не помогает.
  if (sources.length === 0) return null;

  return (
    <>
      {sources.map((source) => (
        <CommerceSource
          key={source.connector_id}
          source={source}
          workspaceSlug={props.workspaceSlug}
          ticketId={props.ticketId}
          onInsertText={props.onInsertText}
        />
      ))}
    </>
  );
}

function CommerceSource(props: {
  source: CommerceContextRow;
  workspaceSlug: string | undefined;
  ticketId: string;
  onInsertText: (text: string) => void;
}): JSX.Element {
  const { source } = props;
  const preview = source.display?.orders_preview ?? 3;
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState(false);
  const [finder, setFinder] = useState(false);
  // Товары, отмеченные для отправки покупателю, — по id, чтобы отметка
  // переживала переключение между подборкой и поиском.
  const [picked, setPicked] = useState<Map<string, CommerceProductRow>>(new Map());

  const orders = source.orders ?? [];
  const shown = expanded ? orders : orders.slice(0, preview);
  const recommendations = source.recommendations ?? [];
  const productActions = (source.quick_actions ?? []).filter((a) => a.scope === 'product');

  const togglePick = (p: CommerceProductRow): void =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(p.external_id)) next.delete(p.external_id);
      else if (next.size < PRODUCT_CARDS_MAX) next.set(p.external_id, p);
      return next;
    });

  return (
    <div className="inbx-contact__section">
      <div className="inbx-contact__section-h">
        {source.connector_name}
        {source.customer?.url && (
          <a
            href={source.customer.url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ marginLeft: 8, fontSize: 'var(--inbx-fz-sm)' }}
          >
            открыть
          </a>
        )}
      </div>

      {source.error && (
        <div
          className="inbx-contact__row"
          style={{ color: 'var(--inbx-danger, #b3261e)', display: 'block' }}
        >
          Источник не ответил: {source.error}
        </div>
      )}

      {!source.error && source.candidates.length > 0 && (
        <CandidatesPicker
          candidates={source.candidates}
          connectorId={source.connector_id}
          workspaceSlug={props.workspaceSlug}
          ticketId={props.ticketId}
        />
      )}

      {!source.error && !source.customer && source.candidates.length === 0 && (
        <div className="inbx-contact__row-label">
          Покупатель по почте и телефону собеседника не найден.
        </div>
      )}

      {source.customer && source.match && (
        <MatchBanner
          source={source}
          workspaceSlug={props.workspaceSlug}
          ticketId={props.ticketId}
        />
      )}

      {source.customer && (
        <>
          {source.customer.name && (
            <div className="inbx-contact__row">
              <span className="inbx-contact__row-label">Покупатель</span>
              <span className="inbx-contact__row-val">{source.customer.name}</span>
            </div>
          )}
          <div className="inbx-contact__row">
            <span className="inbx-contact__row-label">Заказов</span>
            <span className="inbx-contact__row-val">{source.customer.orders_count}</span>
          </div>
          {source.customer.total_spent && (
            <div className="inbx-contact__row">
              <span className="inbx-contact__row-label">На сумму</span>
              <span className="inbx-contact__row-val">
                {formatMoney(source.customer.total_spent)}
              </span>
            </div>
          )}
          {source.customer.tags.length > 0 && (
            <div className="inbx-contact__row">
              <span className="inbx-contact__row-label">Метки</span>
              <span className="inbx-contact__row-val">{source.customer.tags.join(', ')}</span>
            </div>
          )}
        </>
      )}

      {/* Не нашёлся сам — оператор ищет по тому, что продиктовал собеседник:
          номер заказа, почта, телефон. Особенно нужно для маркетплейсов и
          мессенджеров, где почты у собеседника нет вовсе. */}
      {!source.error && !source.customer && (
        <div style={{ marginTop: 8 }}>
          <Button size="sm" variant="secondary" onClick={() => setFinder((v) => !v)}>
            {finder ? 'Скрыть поиск покупателя' : 'Найти покупателя'}
          </Button>
          {finder && (
            <CustomerFinder
              connectorId={source.connector_id}
              workspaceSlug={props.workspaceSlug}
              ticketId={props.ticketId}
            />
          )}
        </div>
      )}

      {shown.map((order) => (
        <OrderCard
          key={order.external_id}
          order={order}
          actions={source.quick_actions ?? []}
          onInsertText={props.onInsertText}
        />
      ))}

      {orders.length > preview && (
        <Button size="sm" variant="secondary" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Свернуть' : `Ещё ${orders.length - preview}`}
        </Button>
      )}

      {/* Что предложить этому покупателю. Показывается только если источник
          такое умеет и действительно что-то подобрал — пустой блок
          «рекомендации» оператору ничего не даёт. */}
      {recommendations.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="inbx-contact__row-label" style={{ marginBottom: 6 }}>
            Можно предложить
          </div>
          {recommendations.map((product) => (
            <ProductCard
              key={product.external_id}
              product={product}
              actions={productActions}
              picked={picked.has(product.external_id)}
              onTogglePick={togglePick}
              onInsertText={props.onInsertText}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <Button size="sm" variant="secondary" onClick={() => setSearch((v) => !v)}>
          {search ? 'Скрыть каталог' : 'Выбрать из каталога'}
        </Button>
      </div>

      {search && (
        <ProductSearch
          workspaceSlug={props.workspaceSlug}
          connectorId={source.connector_id}
          ticketId={props.ticketId}
          actions={productActions}
          picked={picked}
          onTogglePick={togglePick}
          onInsertText={props.onInsertText}
        />
      )}

      {picked.size > 0 && (
        <SendProducts
          workspaceSlug={props.workspaceSlug}
          ticketId={props.ticketId}
          products={[...picked.values()]}
          onSent={() => setPicked(new Map())}
        />
      )}
    </div>
  );
}

/**
 * Насколько можно верить найденному покупателю. Неподтверждённому сервер
 * уже скрыл адрес и трек; здесь — объяснение и способ подтвердить, после
 * того как собеседник назвал то, что знает только покупатель (номер заказа).
 */
function MatchBanner(props: {
  source: CommerceContextRow;
  workspaceSlug: string | undefined;
  ticketId: string;
}): JSX.Element {
  const { match, customer } = props.source;
  const link = useLinkCommerceCustomer(props.workspaceSlug);
  const unlink = useUnlinkCommerceCustomer(props.workspaceSlug);
  if (!match || !customer) return <></>;

  const why: Record<string, string> = {
    verified_identity: 'сайт подтвердил, кто это',
    manual: 'выбран оператором',
    email: 'найден по почте, которую указал сам собеседник',
    phone: 'найден по телефону, который указал сам собеседник',
  };

  return (
    <div
      className="inbx-card"
      style={{
        marginTop: 6,
        padding: 8,
        fontSize: 'var(--inbx-fz-sm)',
        background: match.confirmed ? 'var(--inbx-bg-secondary)' : '#FFF8E6',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div>
        {match.confirmed ? '✓ ' : '⚠ '}
        {why[match.source] ?? match.source}
        {!match.confirmed && '. Адрес и трек скрыты, пока вы не убедитесь, что это он.'}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {!match.confirmed && (
          <Button
            size="sm"
            onClick={() =>
              link.mutate({
                connectorId: props.source.connector_id,
                ticketId: props.ticketId,
                customer,
              })
            }
            disabled={link.isPending}
            title="Например, собеседник назвал номер своего заказа"
          >
            Это он
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            unlink.mutate({ connectorId: props.source.connector_id, ticketId: props.ticketId })
          }
          disabled={unlink.isPending}
        >
          Не он
        </Button>
      </div>
    </div>
  );
}

/** Нашлось несколько покупателей (общий телефон семьи) — выбирает оператор. */
function CandidatesPicker(props: {
  candidates: CommerceCustomerRow[];
  connectorId: string;
  workspaceSlug: string | undefined;
  ticketId: string;
}): JSX.Element {
  const link = useLinkCommerceCustomer(props.workspaceSlug);
  return (
    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div className="inbx-contact__row-label">
        Подходят несколько покупателей — уточните у собеседника и выберите:
      </div>
      {props.candidates.map((c) => (
        <div
          key={c.external_id ?? c.email ?? c.phone ?? ''}
          className="inbx-card"
          style={{ padding: 8, display: 'flex', gap: 8, alignItems: 'center' }}
        >
          <div style={{ flex: 1, minWidth: 0, fontSize: 'var(--inbx-fz-sm)' }}>
            <div>{c.name ?? 'Без имени'}</div>
            <div style={{ color: 'var(--inbx-text-tertiary)' }}>
              {[c.email, c.phone].filter(Boolean).join(' · ')} · заказов: {c.orders_count}
            </div>
          </div>
          <Button
            size="sm"
            onClick={() =>
              link.mutate({ connectorId: props.connectorId, ticketId: props.ticketId, customer: c })
            }
            disabled={link.isPending}
          >
            Это он
          </Button>
        </div>
      ))}
    </div>
  );
}

/**
 * Поиск покупателя по тому, что продиктовал собеседник. Номер заказа —
 * лучший способ: его знает только сам покупатель, поэтому найденного так
 * можно сразу считать подтверждённым.
 */
function CustomerFinder(props: {
  connectorId: string;
  workspaceSlug: string | undefined;
  ticketId: string;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<CommerceCustomerRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const run = useCommerceQuery(props.workspaceSlug);
  const link = useLinkCommerceCustomer(props.workspaceSlug);

  async function search(): Promise<void> {
    setError(null);
    const q = query.trim();
    try {
      if (q.includes('@') || q.replace(/\D/g, '').length >= 10) {
        const isEmail = q.includes('@');
        const res = await run.mutateAsync({
          connectorId: props.connectorId,
          type: 'search_customer',
          params: isEmail ? { email: q, phone: '' } : { email: '', phone: q },
          ticketId: props.ticketId,
        });
        setFound(res.data as CommerceCustomerRow[]);
      } else {
        // Номер заказа: покупатель — тот, на кого оформлен заказ.
        const res = await run.mutateAsync({
          connectorId: props.connectorId,
          type: 'search_order',
          params: { query: q },
          ticketId: props.ticketId,
        });
        setFound(
          (res.data as CommerceOrderRow[])
            .filter((o) => o.customer?.email || o.customer?.phone)
            .map((o) => ({
              external_id: null,
              name: o.customer?.name ?? null,
              email: o.customer?.email ?? null,
              phone: o.customer?.phone ?? null,
              orders_count: 1,
              total_spent: null,
              first_order_at: null,
              last_order_at: null,
              tags: [],
              url: null,
            })),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось найти');
    }
  }

  return (
    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="inbx-input"
          value={query}
          placeholder="номер заказа, почта или телефон"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void search();
          }}
        />
        <Button size="sm" onClick={() => void search()} disabled={run.isPending || !query.trim()}>
          {run.isPending ? '…' : 'Искать'}
        </Button>
      </div>
      {error && <div style={{ color: 'var(--inbx-danger, #b3261e)' }}>{error}</div>}
      {!error && !run.isPending && run.isSuccess && found.length === 0 && (
        <div className="inbx-contact__row-label">Ничего не нашлось.</div>
      )}
      {found.map((c, i) => (
        <div
          key={`${c.email ?? ''}${c.phone ?? ''}${i}`}
          className="inbx-card"
          style={{ padding: 8, display: 'flex', gap: 8, alignItems: 'center' }}
        >
          <div style={{ flex: 1, minWidth: 0, fontSize: 'var(--inbx-fz-sm)' }}>
            <div>{c.name ?? 'Без имени'}</div>
            <div style={{ color: 'var(--inbx-text-tertiary)' }}>
              {[c.email, c.phone].filter(Boolean).join(' · ')}
            </div>
          </div>
          <Button
            size="sm"
            onClick={() =>
              link.mutate({ connectorId: props.connectorId, ticketId: props.ticketId, customer: c })
            }
            disabled={link.isPending}
          >
            Это он
          </Button>
        </div>
      ))}
    </div>
  );
}

/**
 * Отправить отмеченные товары покупателю. В виджете на сайте они придут
 * карточками, в мессенджерах — текстом с ценой и ссылкой.
 */
function SendProducts(props: {
  workspaceSlug: string | undefined;
  ticketId: string;
  products: CommerceProductRow[];
  onSent: () => void;
}): JSX.Element {
  const send = useSendMessage(props.workspaceSlug);
  const [error, setError] = useState<string | null>(null);
  const cards: ProductCardData[] = props.products.map((p) => ({
    external_id: p.external_id,
    title: p.title,
    price: p.price,
    compare_at_price: p.compare_at_price,
    image_url: p.image_url,
    url: p.url,
    in_stock: p.in_stock,
  }));

  return (
    <div
      className="inbx-card"
      style={{
        position: 'sticky',
        bottom: 0,
        marginTop: 10,
        padding: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'var(--inbx-bg)',
      }}
    >
      <span style={{ flex: 1, fontSize: 'var(--inbx-fz-sm)' }}>
        Отмечено товаров: {cards.length}
        {error && <span style={{ color: 'var(--inbx-danger, #b3261e)' }}> · {error}</span>}
      </span>
      <Button
        size="sm"
        disabled={send.isPending}
        onClick={() => {
          setError(null);
          send.mutate(
            {
              ticketId: props.ticketId,
              body: productCardsText(cards),
              meta: { [PRODUCT_CARDS_META_KEY]: cards },
            },
            {
              onSuccess: props.onSent,
              onError: (err) => setError(err instanceof Error ? err.message : 'не отправилось'),
            },
          );
        }}
      >
        {send.isPending ? 'Отправляем…' : 'Отправить покупателю'}
      </Button>
    </div>
  );
}

function OrderCard(props: {
  order: CommerceOrderRow;
  actions: CommerceQuickAction[];
  onInsertText: (text: string) => void;
}): JSX.Element {
  const { order } = props;
  const orderActions = props.actions.filter((a) => a.scope === 'order');

  return (
    <div
      className="inbx-card"
      style={{ marginTop: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ fontSize: 'var(--inbx-fz-sm)' }}>№ {order.number ?? order.external_id}</strong>
        <span
          style={{
            background: STATUS_TONE[order.status] ?? STATUS_TONE.unknown,
            borderRadius: 4,
            padding: '1px 6px',
            fontSize: 'var(--inbx-fz-xs, 11px)',
          }}
          // Оператор должен видеть и исходное слово продавца: наш перевод
          // статуса — догадка по словарю, а спорить с клиентом придётся
          // по тому, что написано в магазине.
          title={order.status_raw ?? undefined}
        >
          {STATUS_LABELS[order.status] ?? order.status}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 'var(--inbx-fz-sm)' }}>
          {formatMoney(order.total)}
        </span>
      </div>

      {order.items.length > 0 && (
        <div style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)' }}>
          {order.items
            .map((i) => (i.quantity > 1 ? `${i.title} × ${i.quantity}` : i.title))
            .join(', ')}
        </div>
      )}

      {order.delivery?.tracking_number && (
        <div style={{ fontSize: 'var(--inbx-fz-sm)' }}>
          Трек: {order.delivery.tracking_number}
        </div>
      )}

      {orderActions.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {orderActions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant="secondary"
              onClick={() => props.onInsertText(renderTemplate(action.template, orderVars(order)))}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductSearch(props: {
  workspaceSlug: string | undefined;
  connectorId: string;
  ticketId: string;
  actions: CommerceQuickAction[];
  picked: Map<string, CommerceProductRow>;
  onTogglePick: (p: CommerceProductRow) => void;
  onInsertText: (text: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<CommerceProductRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const run = useCommerceQuery(props.workspaceSlug);

  async function search(): Promise<void> {
    setError(null);
    try {
      const res = await run.mutateAsync({
        connectorId: props.connectorId,
        type: 'search_product',
        params: { query, limit: 10 },
        ticketId: props.ticketId,
      });
      setItems(res.data as CommerceProductRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось найти товары');
    }
  }

  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="inbx-input"
          value={query}
          placeholder="название или артикул"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void search();
          }}
        />
        <Button size="sm" onClick={() => void search()} disabled={run.isPending || !query.trim()}>
          {run.isPending ? '…' : 'Искать'}
        </Button>
      </div>

      {error && <div style={{ color: 'var(--inbx-danger, #b3261e)' }}>{error}</div>}
      {!error && !run.isPending && items.length === 0 && query && (
        <div className="inbx-contact__row-label">Ничего не нашлось.</div>
      )}

      {items.map((product) => (
        <ProductCard
          key={product.external_id}
          product={product}
          actions={props.actions}
          picked={props.picked.has(product.external_id)}
          onTogglePick={props.onTogglePick}
          onInsertText={props.onInsertText}
        />
      ))}
    </div>
  );
}

/** Одна карточка товара — и в поиске, и в подборке. */
function ProductCard(props: {
  product: CommerceProductRow;
  actions: CommerceQuickAction[];
  picked: boolean;
  onTogglePick: (p: CommerceProductRow) => void;
  onInsertText: (text: string) => void;
}): JSX.Element {
  const { product } = props;
  return (
    <div
      className="inbx-card"
      style={{
        padding: 8,
        marginTop: 4,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        outline: props.picked ? '2px solid var(--inbx-accent)' : undefined,
      }}
    >
      <input
        type="checkbox"
        checked={props.picked}
        onChange={() => props.onTogglePick(product)}
        title="Отправить покупателю карточкой"
        aria-label={`Отметить «${product.title}»`}
      />
      {product.image_url && (
        <img
          src={product.image_url}
          alt=""
          width={32}
          height={32}
          style={{ borderRadius: 4, objectFit: 'cover' }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--inbx-fz-sm)' }}>{product.title}</div>
        <div style={{ fontSize: 'var(--inbx-fz-xs, 11px)', color: 'var(--inbx-text-tertiary)' }}>
          {formatMoney(product.price)}
          {product.in_stock === false && ' · нет в наличии'}
        </div>
      </div>
      {props.actions.map((action) => (
        <Button
          key={action.id}
          size="sm"
          variant="secondary"
          onClick={() => props.onInsertText(renderTemplate(action.template, productVars(product)))}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
}

// ─── подстановка в шаблоны быстрых действий ─────────────────────────────────
//
// Те же имена переменных, что и на сервере (@inbx/shared commerce-template):
// шаблон пишет продавец, и он не должен зависеть от того, где его подставили.

function renderTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key: string) => vars[key] ?? '');
}

function orderVars(order: CommerceOrderRow): Record<string, string> {
  const tracking = order.delivery?.tracking_number ?? '';
  return {
    'order.id': order.external_id,
    'order.number': order.number ?? order.external_id,
    'order.status': order.status,
    'order.status_label': STATUS_LABELS[order.status] ?? order.status,
    'order.status_raw': order.status_raw ?? '',
    'order.total': formatMoney(order.total),
    'order.payment_method': order.payment_method ?? '',
    'order.delivery_method': order.delivery?.method ?? '',
    'order.delivery_address': order.delivery?.address ?? '',
    'order.tracking_number': tracking,
    'order.tracking_url': order.delivery?.tracking_url ?? '',
    'order.eta': order.delivery?.eta ?? '',
    'order.items_line': order.items
      .map((i) => (i.quantity > 1 ? `${i.title} × ${i.quantity}` : i.title))
      .join(', '),
    'order.items_count': String(order.items.length),
    'order.url': order.url ?? '',
    'order.tracking_line': tracking ? `Трек-номер: ${tracking}.` : '',
  };
}

function productVars(product: CommerceProductRow): Record<string, string> {
  return {
    'product.id': product.external_id,
    'product.title': product.title,
    'product.sku': product.sku ?? '',
    'product.price': formatMoney(product.price),
    'product.url': product.url ?? '',
    'product.availability':
      product.in_stock === null ? '' : product.in_stock ? 'в наличии' : 'нет в наличии',
  };
}

const CURRENCY_SIGNS: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€', KZT: '₸' };

function formatMoney(money: CommerceMoney | null): string {
  if (!money) return '';
  const num = Number(money.amount);
  if (!Number.isFinite(num)) return money.amount;
  const sign = CURRENCY_SIGNS[money.currency.toUpperCase()] ?? money.currency;
  const hasKopecks = Math.round(num * 100) % 100 !== 0;
  // Неразрывные пробелы: сумма не должна разрываться переносом строки.
  const body = num
    .toFixed(hasKopecks ? 2 : 0)
    .replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')
    .replace('.', ',');
  return `${body}\u00A0${sign}`;
}
